import { useCallback, useEffect, useRef, useState } from 'react';

import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import { Strategy, StrategyResponse, StrategyStatus } from '~/api-services/AIStrategyBuilderService/types';
import { strategyActionErrorMessage } from '../utils/validation';

const PAGE_SIZE = 20;

export type StrategyListFilters = {
    // The API filters by exactly one status (no comma-joined lists).
    status?: StrategyStatus;
    instrument_code?: string;
    instrument_period?: string;
    side?: 'buy' | 'sell';
    // "<field>,<asc|desc>" per docs/ai-strategy/api-list-sort.md; unset = server default
    // (created,desc — newest first).
    sort?: string;
    // Show only archived strategies (maps to the API's showOnlyArchived; archived items are
    // excluded from the default listing).
    archivedOnly?: boolean;
};

const extractError = (err: unknown): string => {
    if (!err) {
        return 'Unknown error';
    }
    if (typeof err === 'string') {
        return err;
    }
    const anyErr = err as any;
    const data = anyErr?.response?.data;
    if (data) {
        return strategyActionErrorMessage(data, anyErr?.message || 'Unknown error');
    }
    return anyErr?.message || 'Unknown error';
};

// Not a discriminated union: the repo compiles with strict null checks off, where TS won't
// narrow on the `ok` literal, so both fields are optional instead.
export type StrategyActionResult = { ok: boolean; strategy?: Strategy; error?: string };

/**
 * Server-paged strategies list: page-0 refresh (pull-to-refresh), loadMore (infinite scroll)
 * and single-status/instrument filters passed straight to the API. Lifecycle actions patch the
 * affected row in place from the response instead of refetching the whole list; socket-driven
 * progress updates go through patchStrategy.
 */
export const useStrategies = (initialFilters: StrategyListFilters = {}) => {
    const [strategies, setStrategies] = useState<Strategy[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<StrategyListFilters>(initialFilters);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Monotonic request id: a stale response (older refresh racing a filter change) is dropped.
    const requestIdRef = useRef(0);
    // True while a page-0 (replace) request is open, so a quiet refresh can stand down instead of
    // duplicating one. Notably on mount, where the initial load and the screen's first focus event
    // both ask for page 0.
    const replaceInFlightRef = useRef(false);

    const fetchPage = useCallback(
        async (pageToLoad: number, append: boolean, silent = false) => {
            const requestId = ++requestIdRef.current;
            if (append) {
                setLoadingMore(true);
            } else {
                replaceInFlightRef.current = true;
                if (!silent) {
                    setLoading(true);
                    setError(null);
                }
            }
            try {
                const { archivedOnly, ...serverFilters } = filters;
                const { data } = await AIStrategyBuilderService.getStrategies({
                    page: pageToLoad,
                    size: PAGE_SIZE,
                    // Rows need side, TP/SL and the indicator names — all of which live in
                    // frontend_json since 2026-07-26. Roughly a third of the weight of the
                    // full `input` blob this used to request.
                    returnFrontendJSON: true,
                    ...serverFilters,
                    ...(archivedOnly ? { showOnlyArchived: true } : {}),
                });
                if (requestId !== requestIdRef.current) {
                    return;
                }
                if (data?.status && data?.returnData) {
                    const paged = data.returnData;
                    setStrategies((prev) => (append ? [...prev, ...(paged.content || [])] : paged.content || []));
                    setPage(paged.page ?? pageToLoad);
                    setTotalPages(paged.totalPages ?? 0);
                } else if (!silent) {
                    setError(data?.errorCode || 'Failed to load strategies');
                    if (!append) {
                        setStrategies([]);
                    }
                }
                // A failed silent refresh keeps whatever is on screen: the user did not ask for
                // it, so it must not replace a good list with an error state.
            } catch (err) {
                if (requestId !== requestIdRef.current || silent) {
                    return;
                }
                setError(extractError(err));
                if (!append) {
                    setStrategies([]);
                }
            } finally {
                if (append) {
                    // Always clear — even when superseded. A newer request is necessarily a
                    // replace (loadMore is blocked while loadingMore), so nothing else owns
                    // this flag, and skipping it here would wedge pagination for good.
                    setLoadingMore(false);
                } else {
                    if (requestId === requestIdRef.current) {
                        replaceInFlightRef.current = false;
                    }
                    if (!silent && requestId === requestIdRef.current) {
                        setLoading(false);
                    }
                }
            }
        },
        [filters]
    );

    const refresh = useCallback(() => fetchPage(0, false), [fetchPage]);

    // Same page-0 refetch without touching `loading`. `loading` drives the RefreshControl, and
    // on iOS a programmatic `refreshing={true}` expands the spinner tray and shoves the list
    // down — unacceptable for a refresh the user never asked for (screen focus, see the list
    // screen's focus listener). Failures are swallowed for the same reason, and one already in
    // flight is left to finish rather than fired twice.
    const refreshQuietly = useCallback(() => {
        if (replaceInFlightRef.current) {
            return;
        }
        fetchPage(0, false, true);
    }, [fetchPage]);

    const hasMore = page < totalPages - 1;

    const loadMore = useCallback(() => {
        if (loading || loadingMore || page >= totalPages - 1) {
            return;
        }
        fetchPage(page + 1, true);
    }, [fetchPage, loading, loadingMore, page, totalPages]);

    // Initial load + reload whenever the filters change (fetchPage closes over them).
    useEffect(() => {
        refresh();
    }, [refresh]);

    // Merge a partial update (e.g. a socket progress/status event) into the matching row.
    const patchStrategy = useCallback((id: string, patch: Partial<Strategy>) => {
        setStrategies((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    }, []);

    // Run a lifecycle transition and replace the affected row from the response.
    const applyAction = useCallback(
        async (call: Promise<{ data: StrategyResponse }>): Promise<StrategyActionResult> => {
            try {
                const { data } = await call;
                if (data?.status && data.returnData) {
                    const updated = data.returnData;
                    setStrategies((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
                    return { ok: true, strategy: updated };
                }
                const message = strategyActionErrorMessage(data);
                setError(message);
                return { ok: false, error: message };
            } catch (err) {
                const message = extractError(err);
                setError(message);
                return { ok: false, error: message };
            }
        },
        []
    );

    const enable = useCallback((id: string) => applyAction(AIStrategyBuilderService.enableStrategy(id)), [applyAction]);
    const disable = useCallback(
        (id: string) => applyAction(AIStrategyBuilderService.disableStrategy(id)),
        [applyAction]
    );
    const discard = useCallback(
        (id: string) => applyAction(AIStrategyBuilderService.discardStrategy(id)),
        [applyAction]
    );

    // Archiving removes the row from the list (archived strategies are excluded from the
    // default listing) rather than patching it.
    const archive = useCallback(async (id: string): Promise<StrategyActionResult> => {
        try {
            const { data } = await AIStrategyBuilderService.archiveStrategy(id);
            if (data?.status && data.returnData) {
                setStrategies((prev) => prev.filter((s) => s.id !== id));
                return { ok: true, strategy: data.returnData };
            }
            const message = strategyActionErrorMessage(data);
            setError(message);
            return { ok: false, error: message };
        } catch (err) {
            const message = extractError(err);
            setError(message);
            return { ok: false, error: message };
        }
    }, []);

    // Unarchiving (from the archived-only view) likewise removes the row — the strategy moves
    // back to the default listing.
    const unarchive = useCallback(async (id: string): Promise<StrategyActionResult> => {
        try {
            const { data } = await AIStrategyBuilderService.unarchiveStrategy(id);
            if (data?.status && data.returnData) {
                setStrategies((prev) => prev.filter((s) => s.id !== id));
                return { ok: true, strategy: data.returnData };
            }
            const message = strategyActionErrorMessage(data);
            setError(message);
            return { ok: false, error: message };
        } catch (err) {
            const message = extractError(err);
            setError(message);
            return { ok: false, error: message };
        }
    }, []);

    return {
        strategies,
        loading,
        loadingMore,
        error,
        filters,
        setFilters,
        hasMore,
        refresh,
        refreshQuietly,
        loadMore,
        patchStrategy,
        enable,
        disable,
        discard,
        archive,
        unarchive,
    };
};

export default useStrategies;
